import React, { useEffect, useRef, useState, Suspense } from 'react';
import { EditorState, Compartment } from '@codemirror/state';
import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, tooltips } from '@codemirror/view';
import { defaultKeymap, indentWithTab, history, historyKeymap } from '@codemirror/commands';
import { python } from '@codemirror/lang-python';
import { markdown } from '@codemirror/lang-markdown';
import { autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { foldGutter, foldKeymap, bracketMatching } from '@codemirror/language';
import { lintKeymap } from '@codemirror/lint';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import DOMPurify from 'dompurify';
import 'katex/dist/katex.min.css';
import type { NotebookCell } from '../NotebookEditor';
import { propalgos } from './themes/propalgosTheme';
import { pythonCompletions } from './extensions/pythonExtensions';
import { rewriteProxyLink, rewriteProxyUrls, rewriteProxyForAuth, containsProxyContent } from '../../../utils/proxyUrls';

const PlotlyRenderer = React.lazy(() => import('../PlotlyRenderer'));

interface CodeMirrorCellProps {
  cell: NotebookCell;
  isActive: boolean;
  isExecuting: boolean;
  onExecute: (code: string) => Promise<void>;
  onChange: (code: string) => void;
  onFocus: () => void;
  onDelete: () => void;
  onAddCellAbove: () => void;
  onAddCellBelow: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onChangeCellType: (type: 'code' | 'markdown' | 'raw') => void;
  onNavigateUp?: () => void;
  onNavigateDown?: () => void;
  sessionId?: string;
  onApplyFix?: (code: string) => void;
  onInsertCellAbove?: (code: string) => void;
  onClearOutput?: () => void;
  executionTimeMs?: number;
  requestComplete?: (code: string, cursorPos: number) => Promise<{ matches: string[]; cursor_start: number; cursor_end: number } | null>;
  requestInspect?: (code: string, cursorPos: number) => Promise<{ found: boolean; data: Record<string, string> } | null>;
  onRunAbove?: () => void;
  onRunBelow?: () => void;
  onSplitCell?: (cursorPos: number) => void;
  isQueued?: boolean;
  onUpdateTags?: (tags: string[]) => void;
  proxyAuthCode?: string | null;
  getProxyAuthCode?: () => Promise<string | null>;
  /** Callback for AI cell Q&A — applies code suggestion to cell */
  onApplyCodeSuggestion?: (code: string) => void;
  shouldFocusEditor?: boolean;
  onFocusHandled?: () => void;
}

export const CodeMirrorCell = React.memo<CodeMirrorCellProps>(({
  cell,
  isActive,
  isExecuting,
  onExecute,
  onChange,
  onFocus,
  onDelete,
  onAddCellAbove,
  onAddCellBelow,
  onMoveUp,
  onMoveDown,
  onChangeCellType,
  onNavigateUp,
  onNavigateDown,
  sessionId,
  onApplyFix,
  onInsertCellAbove,
  onClearOutput,
  executionTimeMs,
  requestComplete,
  requestInspect,
  onRunAbove,
  onRunBelow,
  onSplitCell,
  isQueued,
  onUpdateTags,
  proxyAuthCode,
  getProxyAuthCode,
  onApplyCodeSuggestion,
  shouldFocusEditor,
  onFocusHandled,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  // Code and raw cells start in editing mode, markdown cells start in preview mode
  const [isEditing, setIsEditing] = useState(cell.type !== 'markdown');
  const [cellValue, setCellValue] = useState(cell.source);
  const [outputCollapsed, setOutputCollapsed] = useState(false);
  const [inspectTooltip, setInspectTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagInputValue, setTagInputValue] = useState('');
  const tagInputRef = useRef<HTMLInputElement>(null);
  const languageConf = useRef(new Compartment());
  const completionConf = useRef(new Compartment());
  const [lastKeyPress, setLastKeyPress] = useState<{ key: string; time: number } | null>(null);

  // Inline AI assistant state
  const [aiAskOpen, setAiAskOpen] = useState(false);
  const [aiAskQuestion, setAiAskQuestion] = useState('');
  const [aiAskLoading, setAiAskLoading] = useState(false);
  const [aiAskResult, setAiAskResult] = useState<{ answer: string; code_suggestion?: string | null; usage: { latency_ms: number; cost_usd: number } } | null>(null);
  const [aiAskError, setAiAskError] = useState<string | null>(null);
  const [aiAskSelection, setAiAskSelection] = useState<string | null>(null);
  const aiAskInputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;

  // Store callbacks in refs so they can be updated without recreating the editor
  const callbackRefs = useRef({
    onExecute,
    onNavigateUp,
    onNavigateDown,
    onAddCellBelow,
    requestComplete,
    requestInspect,
    onSplitCell,
  });

  // Update refs when props change
  useEffect(() => {
    callbackRefs.current = {
      onExecute,
      onNavigateUp,
      onNavigateDown,
      onAddCellBelow,
      requestComplete,
      requestInspect,
      onSplitCell,
    };
  }, [onExecute, onNavigateUp, onNavigateDown, onAddCellBelow, requestComplete, requestInspect, onSplitCell]);

  useEffect(() => {
    if (!editorRef.current || viewRef.current) return;

    let onChangeDebounceId: ReturnType<typeof setTimeout> | null = null;

    // Custom keymap for Jupyter-like behavior
    const jupyterKeymap = keymap.of([
      {
        key: 'Shift-Enter',
        run: () => {
          if (cell.type === 'code') {
            callbackRefs.current.onExecute(viewRef.current?.state.doc.toString() || '');
          } else {
            // For markdown cells, exit edit mode to show the rendered markdown
            setIsEditing(false);
          }
          callbackRefs.current.onNavigateDown?.();
          return true;
        },
      },
      {
        key: 'Ctrl-Enter',
        mac: 'Cmd-Enter',
        run: () => {
          if (cell.type === 'code') {
            callbackRefs.current.onExecute(viewRef.current?.state.doc.toString() || '');
          } else {
            // For markdown cells, just exit edit mode without moving
            setIsEditing(false);
          }
          return true;
        },
      },
      {
        key: 'Alt-Enter',
        run: () => {
          if (cell.type === 'code') {
            callbackRefs.current.onExecute(viewRef.current?.state.doc.toString() || '');
          } else {
            // For markdown cells, exit edit mode to render
            setIsEditing(false);
          }
          callbackRefs.current.onAddCellBelow();
          return true;
        },
      },
      {
        key: 'Shift-Tab',
        run: (view) => {
          const pos = view.state.selection.main.head;
          const code = view.state.doc.toString();
          const inspectFn = callbackRefs.current.requestInspect;
          if (!inspectFn) return false;
          inspectFn(code, pos).then(result => {
            if (result?.found && result.data['text/plain']) {
              const coords = view.coordsAtPos(pos);
              if (coords) {
                setInspectTooltip({
                  text: result.data['text/plain'],
                  x: coords.left,
                  y: coords.top,
                });
                // Auto-dismiss after 8s
                setTimeout(() => setInspectTooltip(null), 8000);
              }
            }
          });
          return true;
        },
      },
      {
        key: 'Ctrl-l',
        mac: 'Cmd-l',
        run: (view) => {
          const sel = view.state.selection.main;
          const selectedText = sel.from !== sel.to ? view.state.sliceDoc(sel.from, sel.to) : null;
          setAiAskSelection(selectedText);
          setAiAskOpen(true);
          setAiAskResult(null);
          setAiAskError(null);
          setAiAskQuestion('');
          setTimeout(() => aiAskInputRef.current?.focus(), 50);
          return true;
        },
      },
      {
        key: 'Escape',
        run: () => {
          if (aiAskOpen) {
            setAiAskOpen(false);
            return true;
          }
          setIsEditing(false);
          setInspectTooltip(null);
          editorRef.current?.blur();
          return true;
        },
      },
      {
        key: 'Ctrl-Shift--',
        mac: 'Cmd-Shift--',
        run: (view) => {
          const pos = view.state.selection.main.head;
          callbackRefs.current.onSplitCell?.(pos);
          return true;
        },
      },
      {
        key: 'ArrowUp',
        run: (view) => {
          const cursor = view.state.selection.main.head;
          const line = view.state.doc.lineAt(cursor);
          if (line.number === 1 && callbackRefs.current.onNavigateUp) {
            callbackRefs.current.onNavigateUp();
            return true;
          }
          return false;
        },
      },
      {
        key: 'ArrowDown',
        run: (view) => {
          const cursor = view.state.selection.main.head;
          const line = view.state.doc.lineAt(cursor);
          if (line.number === view.state.doc.lines && callbackRefs.current.onNavigateDown) {
            callbackRefs.current.onNavigateDown();
            return true;
          }
          return false;
        },
      },
    ]);

    // Kernel-backed completion source: calls requestComplete, falls back to static
    const kernelCompletionSource = async (context: any) => {
      const completeFn = callbackRefs.current.requestComplete;
      if (completeFn) {
        const code = context.state.doc.toString();
        const pos = context.pos;
        try {
          const result = await completeFn(code, pos);
          if (result && result.matches.length > 0) {
            return {
              from: result.cursor_start,
              options: result.matches.map((m: string) => ({
                label: m,
                type: 'variable',
              })),
            };
          }
        } catch {
          // Fall through to static completions
        }
      }
      // Fall back to static completions
      return pythonCompletions(context);
    };

    // Use kernel-backed completions with static fallback
    const enhancedCompletions = completionConf.current.of(
      autocompletion({
        override: [kernelCompletionSource],
        activateOnTyping: true,
      })
    );

    const extensions = [
      lineNumbers(),
      highlightActiveLine(),
      highlightActiveLineGutter(),
      highlightSelectionMatches(),
      bracketMatching(),
      closeBrackets(),
      foldGutter(),
      history(),
      // Fixed positioning prevents autocomplete/tooltip clipping by overflow:hidden ancestors
      tooltips({ position: 'fixed' }),
      languageConf.current.of(cell.type === 'code' ? python() : markdown()),
      enhancedCompletions,
      jupyterKeymap,
      keymap.of([
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...historyKeymap,
        ...searchKeymap,
        ...completionKeymap,
        ...foldKeymap,
        ...lintKeymap,
        indentWithTab,
      ]),
      propalgos, // Use our custom PropAlgos theme
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          const newValue = update.state.doc.toString();
          setCellValue(newValue);
          // Debounce parent state sync to reduce re-renders during rapid typing
          if (onChangeDebounceId !== null) clearTimeout(onChangeDebounceId);
          onChangeDebounceId = setTimeout(() => {
            onChange(newValue);
            onChangeDebounceId = null;
          }, 300);
        }
      }),
    ];

    const state = EditorState.create({
      doc: cell.source,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: editorRef.current,
    });

    viewRef.current = view;

    return () => {
      if (onChangeDebounceId !== null) clearTimeout(onChangeDebounceId);
      view.destroy();
      viewRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update content when cell source changes externally
  useEffect(() => {
    if (viewRef.current && cell.source !== viewRef.current.state.doc.toString()) {
      viewRef.current.dispatch({
        changes: {
          from: 0,
          to: viewRef.current.state.doc.length,
          insert: cell.source,
        },
      });
      setCellValue(cell.source);
    }
  }, [cell.source]);

  // Update language based on cell type
  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.dispatch({
        effects: languageConf.current.reconfigure(
          cell.type === 'code' ? python() : markdown()
        ),
      });
    }
  }, [cell.type]);

  // Focus management
  useEffect(() => {
    if (isActive && isEditing && viewRef.current) {
      viewRef.current.focus();
    }
    // If cell loses focus and it's markdown, go back to preview mode
    if (!isActive && cell.type === 'markdown') {
      setIsEditing(false);
    }
  }, [isActive, isEditing, cell.type]);

  // Handle programmatic focus requests (e.g., Shift+Enter navigation)
  useEffect(() => {
    if (shouldFocusEditor && viewRef.current) {
      setIsEditing(true);
      requestAnimationFrame(() => {
        viewRef.current?.focus();
        onFocusHandled?.();
      });
    }
  }, [shouldFocusEditor, onFocusHandled]);

  // Listen for command palette "Ask AI" trigger on the active cell
  useEffect(() => {
    if (!isActive) return;
    const handler = () => {
      const sel = viewRef.current?.state.selection.main;
      const selectedText = sel && sel.from !== sel.to ? viewRef.current!.state.sliceDoc(sel.from, sel.to) : null;
      setAiAskSelection(selectedText);
      setAiAskOpen(true);
      setAiAskResult(null);
      setAiAskError(null);
      setAiAskQuestion('');
      setTimeout(() => aiAskInputRef.current?.focus(), 50);
    };
    document.addEventListener('propalgos:toggle-inline-ai', handler);
    return () => document.removeEventListener('propalgos:toggle-inline-ai', handler);
  }, [isActive]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isEditing && isActive) {
      switch (e.key) {
        case 'Enter':
          setIsEditing(true);
          setTimeout(() => {
            viewRef.current?.focus();
          }, 50);
          break;
        case 'a':
          if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            onAddCellAbove();
          }
          break;
        case 'b':
          if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            onAddCellBelow();
          }
          break;
        case 'd':
          if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            const now = Date.now();
            // Check if 'd' was pressed twice within 500ms
            if (lastKeyPress && lastKeyPress.key === 'd' && (now - lastKeyPress.time) < 500) {
              onDelete();
              setLastKeyPress(null);
            } else {
              setLastKeyPress({ key: 'd', time: now });
              // Clear after timeout
              setTimeout(() => setLastKeyPress(null), 500);
            }
          }
          break;
        case 'm':
          if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            onChangeCellType('markdown');
          }
          break;
        case 'y':
          if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            onChangeCellType('code');
          }
          break;
        case 'r':
          if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            onChangeCellType('raw');
          }
          break;
        case 'o':
          if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            setOutputCollapsed(prev => !prev);
          }
          break;
        case 'l':
          if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            // Toggle line numbers on this cell's editor
            if (viewRef.current) {
              const hasLineNums = viewRef.current.state.facet(EditorView.decorations) !== undefined;
              // Toggle via dispatch — using gutter visibility
              const dom = viewRef.current.dom.querySelector('.cm-lineNumbers');
              if (dom) {
                (dom as HTMLElement).style.display = (dom as HTMLElement).style.display === 'none' ? '' : 'none';
              }
            }
          }
          break;
        case '1': case '2': case '3': case '4': case '5': case '6':
          if (!e.shiftKey && !e.ctrlKey && !e.metaKey && cell.type === 'markdown') {
            e.preventDefault();
            const level = parseInt(e.key);
            const prefix = '#'.repeat(level) + ' ';
            // Prepend heading to markdown cell
            const currentSource = viewRef.current?.state.doc.toString() || cellValue;
            const stripped = currentSource.replace(/^#{1,6}\s*/, '');
            const newSource = prefix + stripped;
            onChange(newSource);
            if (viewRef.current) {
              viewRef.current.dispatch({
                changes: { from: 0, to: viewRef.current.state.doc.length, insert: newSource },
              });
            }
          }
          break;
      }
    }
  };

  const executionCountDisplay = cell.execution_count
    ? `In [${cell.execution_count}]:`
    : (isExecuting || isQueued)
    ? 'In [*]:'
    : 'In [ ]:';

  return (
    <div
      className={`group relative mb-3 border-l-[3px] ${
        cell.type === 'raw' && isActive ? 'border-l-gray-500 shadow-lg shadow-gray-500/10' :
        isActive && isEditing ? 'border-l-green-500 shadow-lg shadow-green-500/20' :
        isActive ? 'border-l-blue-500 shadow-lg shadow-blue-500/20' :
        'border-l-transparent'
      }`}
      onClick={() => {
        onFocus();
        if (!isEditing && (cell.type === 'code' || cell.type === 'raw')) {
          setIsEditing(true);
        }
      }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="flex">
        {/* Execution count + mode indicator - only for code cells */}
        {cell.type === 'code' && (
          <div className="w-20 flex-shrink-0 pt-3 text-xs text-gray-500 font-mono text-right pr-3">
            {executionCountDisplay}
            {isActive && (
              <div className={`mt-1 text-[10px] uppercase tracking-wider ${
                isEditing ? 'text-green-500' : 'text-blue-400'
              }`}>
                {isEditing ? 'edit' : 'cmd'}
              </div>
            )}
          </div>
        )}

        {/* Cell content with glassy effect and sharp corners — no overflow-hidden so CodeMirror tooltips/autocomplete can render outside bounds */}
        <div className={`flex-1 bg-black/50 border border-gray-800/50 shadow-lg ${cell.type !== 'code' ? 'ml-20' : ''}`}>
          {/* Minimalist cell controls - always visible when active */}
          {isActive && (
            <div className="absolute top-0 right-0 flex items-center space-x-2 p-2 z-10">
              {/* Mode badge for markdown cells (code cells show it in the gutter) */}
              {cell.type === 'markdown' && (
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${
                  isEditing ? 'text-green-500' : 'text-blue-400'
                }`}>
                  {isEditing ? 'edit' : 'cmd'}
                </span>
              )}
              {/* Cell type toggle */}
              <select
                value={cell.type}
                onChange={(e) => {
                  e.stopPropagation();
                  onChangeCellType(e.target.value as 'code' | 'markdown' | 'raw');
                }}
                className="text-xs px-1.5 py-1 bg-gray-800/80 text-gray-400 hover:text-white hover:bg-gray-700 backdrop-blur-sm border border-gray-700/50 transition-all cursor-pointer appearance-none"
                title="Cell type (M/Y/R keys in command mode)"
                onClick={(e) => e.stopPropagation()}
              >
                <option value="code">Code</option>
                <option value="markdown">Markdown</option>
                <option value="raw">Raw</option>
              </select>
              
              {/* Delete button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="text-xs px-2 py-1 bg-gray-800/80 text-gray-400 hover:text-red-400 hover:bg-red-900/50 backdrop-blur-sm border border-gray-700/50 transition-all"
                title="Delete cell (DD in command mode)"
              >
                ×
              </button>
            </div>
          )}

          {/* Always render editor but hide it when showing markdown preview */}
          <div
            ref={editorRef}
            className="cm-editor-container"
            style={{ display: cell.type === 'markdown' && !isEditing ? 'none' : 'block' }}
          />

          {/* Raw cell label */}
          {cell.type === 'raw' && (
            <div className="px-3 py-1 bg-gray-800/30 border-t border-gray-800/50 text-[10px] text-gray-500 uppercase tracking-wider">
              Raw (not executed or rendered)
            </div>
          )}
          
          {/* Markdown preview overlay */}
          {cell.type === 'markdown' && !isEditing && (
            <div 
              className="p-4 markdown-preview cursor-text"
              onClick={(e) => {
                e.stopPropagation();
                onFocus();
                setIsEditing(true);
                // Need a small delay to ensure the editor is visible before focusing
                setTimeout(() => {
                  viewRef.current?.focus();
                  // Place cursor at the end of the content
                  const length = viewRef.current?.state.doc.length || 0;
                  viewRef.current?.dispatch({
                    selection: { anchor: length, head: length }
                  });
                }, 50);
              }}
            >
              {cellValue.trim() ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  components={{
                    a: ({ href, children, ...props }) => {
                      const rewritten = sessionId && href ? rewriteProxyLink(href, sessionId) : null;
                      return (
                        <a
                          href={rewritten || href}
                          target={rewritten ? '_blank' : undefined}
                          rel={rewritten ? 'noopener noreferrer' : undefined}
                          {...props}
                        >
                          {children}
                        </a>
                      );
                    },
                  }}
                >
                  {cellValue}
                </ReactMarkdown>
              ) : (
                <p className="text-gray-500 italic">Click to edit markdown...</p>
              )}
            </div>
          )}

          {/* Run button for code cells */}
          {cell.type === 'code' && (
            <div className="flex justify-between items-center px-3 py-2 bg-black/30 border-t border-gray-800/50 backdrop-blur-sm">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onExecute(cellValue);  // Use current editor value, not initial source
                }}
                disabled={isExecuting || isQueued}
                className="px-3 py-1 text-xs bg-gray-700/50 text-white hover:bg-gray-700/80 disabled:bg-gray-600/30 disabled:cursor-not-allowed flex items-center space-x-1 backdrop-blur-sm transition-all border border-gray-600/30"
              >
                {isExecuting ? (
                  <>
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Running...</span>
                  </>
                ) : isQueued ? (
                  <>
                    <span>⏳</span>
                    <span>Queued</span>
                  </>
                ) : (
                  <>
                    <span>▶</span>
                    <span>Run</span>
                  </>
                )}
              </button>
              <div className="flex items-center gap-3">
                {executionTimeMs !== undefined && !isExecuting && (
                  <span className="text-xs text-gray-500 font-mono">
                    {executionTimeMs < 1000 ? `${executionTimeMs}ms` : `${(executionTimeMs / 1000).toFixed(1)}s`}
                  </span>
                )}
                <span className="text-xs text-gray-500">Shift+Enter to run · {typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) ? '⌘' : 'Ctrl'}+L for AI</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cell tags */}
      {isActive && onUpdateTags && (
        <CellTagsBar
          tags={cell.metadata?.tags || []}
          onUpdateTags={onUpdateTags}
          showTagInput={showTagInput}
          setShowTagInput={setShowTagInput}
          tagInputValue={tagInputValue}
          setTagInputValue={setTagInputValue}
          tagInputRef={tagInputRef}
        />
      )}

      {/* Inspect tooltip */}
      {inspectTooltip && (
        <div
          className="fixed z-50 max-w-lg max-h-64 overflow-auto bg-gray-900 border border-gray-700 shadow-xl p-3 text-xs text-gray-300 font-mono whitespace-pre-wrap"
          style={{ left: inspectTooltip.x, top: inspectTooltip.y - 8, transform: 'translateY(-100%)' }}
          onClick={() => setInspectTooltip(null)}
        >
          {inspectTooltip.text}
        </div>
      )}

      {/* Inline AI assistant panel (Cmd+L / Ctrl+L) */}
      {aiAskOpen && cell.type === 'code' && (
        <InlineCellAI
          question={aiAskQuestion}
          setQuestion={setAiAskQuestion}
          selection={aiAskSelection}
          cellCode={cellValue}
          loading={aiAskLoading}
          result={aiAskResult}
          error={aiAskError}
          inputRef={aiAskInputRef}
          onSubmit={async () => {
            if (!aiAskQuestion.trim() || aiAskLoading) return;
            setAiAskLoading(true);
            setAiAskError(null);
            setAiAskResult(null);
            try {
              const token = localStorage.getItem('authToken');
              const { getApiUrl } = await import('../../../utils/api');
              const res = await fetch(`${getApiUrl()}/api/v1/ai/cell/ask`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                  question: aiAskQuestion,
                  cell_code: cellValue,
                  selected_text: aiAskSelection,
                }),
              });
              if (res.status === 402) { setAiAskError('Insufficient balance for AI features.'); return; }
              if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Request failed (${res.status})`); }
              setAiAskResult(await res.json());
            } catch (e: any) {
              setAiAskError(e.message || 'AI question failed');
            } finally {
              setAiAskLoading(false);
            }
          }}
          onClose={() => { setAiAskOpen(false); viewRef.current?.focus(); }}
          onApplyCode={(code) => { if (onApplyCodeSuggestion) { onApplyCodeSuggestion(code); } else { onChange(code); } setAiAskOpen(false); viewRef.current?.focus(); }}
        />
      )}

      {/* Output area - only for code cells */}
      {cell.type === 'code' && cell.outputs && cell.outputs.length > 0 && (
        <div className="ml-20 mt-2">
          <div className="flex justify-between items-center mb-1">
            <button
              onClick={(e) => { e.stopPropagation(); setOutputCollapsed(prev => !prev); }}
              className="text-[10px] text-gray-500 hover:text-gray-300 px-1.5 py-0.5 hover:bg-gray-800/50 transition-colors"
              title={outputCollapsed ? 'Show output (O key)' : 'Hide output (O key)'}
            >
              {outputCollapsed ? 'show output' : 'hide output'}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const text = cell.outputs?.map((o: any) => {
                    if (o.type === 'stream') return o.text;
                    if (o.data?.['text/plain']) return o.data['text/plain'];
                    return '';
                  }).join('') || '';
                  navigator.clipboard.writeText(text);
                }}
                className="text-[10px] text-gray-500 hover:text-gray-300 px-1.5 py-0.5 hover:bg-gray-800/50 transition-colors"
                title="Copy output to clipboard"
              >
                copy
              </button>
              {onClearOutput && (
                <button
                  onClick={(e) => { e.stopPropagation(); onClearOutput(); }}
                  className="text-[10px] text-gray-500 hover:text-gray-300 px-1.5 py-0.5 hover:bg-gray-800/50 transition-colors"
                  title="Clear cell output"
                >
                  clear
                </button>
              )}
            </div>
          </div>
          {!outputCollapsed && (
            <div className="max-h-[500px] overflow-y-auto">
              <OutputArea outputs={cell.outputs} executionCount={cell.execution_count} sessionId={sessionId} cellSource={cell.source} onApplyFix={onApplyFix} onInsertCellAbove={onInsertCellAbove} proxyAuthCode={proxyAuthCode} getProxyAuthCode={getProxyAuthCode} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}, (prev, next) => (
  prev.cell.id === next.cell.id &&
  prev.cell.source === next.cell.source &&
  prev.cell.type === next.cell.type &&
  prev.cell.outputs === next.cell.outputs &&
  prev.cell.execution_count === next.cell.execution_count &&
  prev.isActive === next.isActive &&
  prev.isExecuting === next.isExecuting &&
  prev.isQueued === next.isQueued &&
  prev.sessionId === next.sessionId &&
  prev.executionTimeMs === next.executionTimeMs &&
  prev.proxyAuthCode === next.proxyAuthCode
));

const COMMON_TAGS = ['parameters', 'hide-input', 'hide-output', 'skip', 'active-ipynb'];

const CellTagsBar: React.FC<{
  tags: string[];
  onUpdateTags: (tags: string[]) => void;
  showTagInput: boolean;
  setShowTagInput: (v: boolean) => void;
  tagInputValue: string;
  setTagInputValue: (v: string) => void;
  tagInputRef: React.RefObject<HTMLInputElement>;
}> = ({ tags, onUpdateTags, showTagInput, setShowTagInput, tagInputValue, setTagInputValue, tagInputRef }) => {
  const addTag = (tag: string) => {
    const trimmed = tag.trim().toLowerCase().replace(/\s+/g, '-');
    if (!trimmed || tags.includes(trimmed)) return;
    onUpdateTags([...tags, trimmed]);
    setTagInputValue('');
    setShowTagInput(false);
  };

  const removeTag = (tag: string) => {
    onUpdateTags(tags.filter(t => t !== tag));
  };

  // Suggestions: common tags not already applied
  const suggestions = COMMON_TAGS.filter(t => !tags.includes(t));

  return (
    <div className="ml-20 flex items-center gap-1.5 px-2 py-1 min-h-[28px]">
      {tags.map(tag => (
        <span
          key={tag}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-purple-900/30 text-purple-300 border border-purple-700/30 rounded-sm"
        >
          {tag}
          <button
            onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
            className="text-purple-500 hover:text-purple-200 ml-0.5"
          >
            &times;
          </button>
        </span>
      ))}
      {showTagInput ? (
        <div className="relative">
          <input
            ref={tagInputRef}
            value={tagInputValue}
            onChange={(e) => setTagInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { addTag(tagInputValue); e.stopPropagation(); }
              if (e.key === 'Escape') { setShowTagInput(false); setTagInputValue(''); e.stopPropagation(); }
              e.stopPropagation();
            }}
            onBlur={() => { if (tagInputValue.trim()) addTag(tagInputValue); else setShowTagInput(false); }}
            placeholder="tag name"
            className="w-24 px-1.5 py-0.5 text-[10px] bg-gray-900/80 text-gray-300 border border-gray-700/50 rounded-sm outline-none focus:border-purple-500/50"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
          {suggestions.length > 0 && !tagInputValue && (
            <div className="absolute top-full left-0 mt-0.5 bg-gray-900 border border-gray-700 shadow-lg z-50 min-w-[120px]">
              {suggestions.map(s => (
                <button
                  key={s}
                  onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
                  className="block w-full px-2 py-1 text-[10px] text-gray-400 hover:bg-white/5 hover:text-white text-left"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); setShowTagInput(true); }}
          className="text-[10px] text-gray-600 hover:text-gray-400 px-1 py-0.5 transition-colors"
          title="Add tag"
        >
          + tag
        </button>
      )}
    </div>
  );
};

// Strip ANSI escape codes (colors, cursor movement, progress bars)
const stripAnsi = (text: string): string =>
  text.replace(/\x1b\[[0-9;]*[a-zA-Z]|\x1b\].*?\x07|\r/g, '');

// Consolidate consecutive stream outputs into groups for cleaner rendering
const groupOutputs = (outputs: any[]): any[] => {
  const groups: any[] = [];
  for (const output of outputs) {
    const prev = groups[groups.length - 1];
    if (output.type === 'stream' && prev?.type === 'stream' && output.name === prev.name) {
      prev.text += output.text;
    } else if (output.type === 'stream') {
      groups.push({ ...output, text: output.text });
    } else {
      groups.push(output);
    }
  }
  return groups;
};

// Proxy-safe DOMPurify config for iframes
const PROXY_PURIFY_CONFIG = {
  ADD_TAGS: ['style', 'iframe'],
  ADD_ATTR: ['target', 'rel', 'src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'sandbox'],
};

let cmPurifyHookInstalled = false;
function installCmProxyIframeHook() {
  if (cmPurifyHookInstalled) return;
  cmPurifyHookInstalled = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName !== 'IFRAME') return;
    const src = node.getAttribute('src') || '';
    if (!(/\/proxy\/\d+\//.test(src) || /proxy-auth/.test(src) || /\/_auth\?/.test(src))) {
      node.remove();
    }
  });
}

// Renders HTML output with proxy auth rewriting and iframe support
const HtmlProxyOutput: React.FC<{
  html: string;
  sessionId?: string;
  proxyAuthCode?: string | null;
  getProxyAuthCode?: () => Promise<string | null>;
}> = ({ html, sessionId, proxyAuthCode: _parentCode, getProxyAuthCode }) => {
  const [freshAuthCode, setFreshAuthCode] = useState<string | null>(null);

  installCmProxyIframeHook();

  const hasProxy = containsProxyContent(html);

  // Always fetch a fresh auth code when proxy content is detected.
  // The parent proxyAuthCode was generated at session init and is almost
  // certainly expired (60s TTL) by the time a Streamlit/Bokeh cell runs.
  useEffect(() => {
    if (!hasProxy || freshAuthCode || !getProxyAuthCode) return;
    let cancelled = false;
    getProxyAuthCode().then(code => {
      if (!cancelled && code) setFreshAuthCode(code);
    });
    return () => { cancelled = true; };
  }, [hasProxy, freshAuthCode, getProxyAuthCode]);

  let processed = sessionId ? rewriteProxyUrls(html, sessionId) : html;
  const authCode = freshAuthCode;
  if (sessionId && authCode && hasProxy) {
    processed = rewriteProxyForAuth(processed, sessionId, authCode);
  }
  const clean = DOMPurify.sanitize(processed, PROXY_PURIFY_CONFIG);

  return (
    <div
      className="prose prose-invert max-w-none jupyter-html-output"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

// Separate output rendering component with sharp, glassy styling
const OutputArea: React.FC<{
  outputs: any[];
  executionCount?: number | null;
  sessionId?: string;
  cellSource?: string;
  onApplyFix?: (code: string) => void;
  onInsertCellAbove?: (code: string) => void;
  proxyAuthCode?: string | null;
  getProxyAuthCode?: () => Promise<string | null>;
}> = ({ outputs, executionCount, sessionId, cellSource, onApplyFix, onInsertCellAbove, proxyAuthCode, getProxyAuthCode }) => {
  const grouped = groupOutputs(outputs);
  return (
    <div className="space-y-2">
      {grouped.map((output, idx) => (
        <div key={idx} className="relative">
          {output.type === 'execute_result' && executionCount && (
            <div className="text-xs text-gray-500 font-mono mb-1">Out [{executionCount}]:</div>
          )}
          <div className="bg-black/50 border border-gray-800/50 backdrop-blur-sm p-3 text-sm text-left shadow">
            {output.type === 'stream' && (
              <pre className={`whitespace-pre-wrap text-left font-mono ${
                output.name === 'stderr' ? 'text-red-400' : 'text-white'
              }`}>{stripAnsi(output.text)}</pre>
            )}

            {(output.type === 'execute_result' || output.type === 'display_data') && (
              <>
                {output.data['application/vnd.plotly.v1+json'] ? (
                  <Suspense fallback={
                    <div className="p-4 bg-gray-800 rounded text-gray-400 text-sm animate-pulse">Loading chart...</div>
                  }>
                    <PlotlyRenderer data={output.data['application/vnd.plotly.v1+json']} />
                  </Suspense>
                ) : output.data['text/html'] ? (
                  <HtmlProxyOutput
                    html={output.data['text/html']}
                    sessionId={sessionId}
                    proxyAuthCode={proxyAuthCode}
                    getProxyAuthCode={getProxyAuthCode}
                  />
                ) : output.data['image/png'] ? (
                  <div className="relative group/img inline-block">
                    <img
                      src={`data:image/png;base64,${output.data['image/png']}`}
                      alt="Output"
                      className="max-w-full"
                    />
                    <a
                      href={`data:image/png;base64,${output.data['image/png']}`}
                      download="output.png"
                      className="absolute top-2 right-2 p-1.5 bg-gray-900/80 text-gray-400 hover:text-white hover:bg-gray-800 border border-gray-700/50 backdrop-blur-sm transition-all opacity-0 group-hover/img:opacity-100"
                      title="Download image"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </a>
                  </div>
                ) : output.data['text/plain'] ? (
                  <pre className="text-white whitespace-pre-wrap font-mono">
                    {output.data['text/plain']}
                  </pre>
                ) : null}
              </>
            )}

            {output.type === 'error' && (
              <ErrorOutput output={output} cellSource={cellSource} onApplyFix={onApplyFix} onInsertCellAbove={onInsertCellAbove} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Inline AI Q&A panel — triggered by Cmd+L / Ctrl+L inside a code cell
const InlineCellAI: React.FC<{
  question: string;
  setQuestion: (q: string) => void;
  selection: string | null;
  cellCode: string;
  loading: boolean;
  result: { answer: string; code_suggestion?: string | null; usage: { latency_ms: number; cost_usd: number } } | null;
  error: string | null;
  inputRef: React.RefObject<HTMLInputElement>;
  onSubmit: () => void;
  onClose: () => void;
  onApplyCode: (code: string) => void;
}> = ({ question, setQuestion, selection, loading, result, error, inputRef, onSubmit, onClose, onApplyCode }) => (
  <div className="ml-20 my-1 bg-indigo-950/40 border border-indigo-800/30 rounded-lg overflow-hidden" onClick={e => e.stopPropagation()}>
    {/* Header */}
    <div className="flex items-center justify-between px-3 py-1.5 bg-indigo-950/60">
      <div className="flex items-center gap-1.5 text-xs text-indigo-300">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Ask AI
        {selection && <span className="text-indigo-400/60 ml-1">(selection)</span>}
      </div>
      <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xs px-1">Esc</button>
    </div>

    {/* Input */}
    <div className="flex items-center gap-2 px-3 py-2 border-b border-indigo-800/20">
      <input
        ref={inputRef}
        type="text"
        value={question}
        onChange={e => setQuestion(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit(); }
          if (e.key === 'Escape') onClose();
          e.stopPropagation();
        }}
        placeholder={selection ? 'Ask about this selection...' : 'Ask about this code...'}
        className="flex-1 bg-transparent text-sm text-gray-200 placeholder-gray-500 outline-none"
        disabled={loading}
      />
      <button
        onClick={onSubmit}
        disabled={loading || !question.trim()}
        className="px-2 py-1 bg-indigo-700/60 text-indigo-200 rounded text-xs hover:bg-indigo-600/70 transition-colors disabled:opacity-40"
      >
        {loading ? (
          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
            <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        ) : 'Ask'}
      </button>
    </div>

    {/* Error */}
    {error && (
      <div className="px-3 py-2 text-xs text-red-300 bg-red-950/30">{error}</div>
    )}

    {/* Result */}
    {result && (
      <div className="px-3 py-2 space-y-2">
        <div className="text-xs text-gray-200 whitespace-pre-wrap leading-relaxed">{result.answer}</div>
        {result.code_suggestion && (
          <div>
            <pre className="text-xs bg-gray-900 text-gray-200 p-2 rounded overflow-x-auto mb-1.5">{result.code_suggestion}</pre>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onApplyCode(result.code_suggestion!)}
                className="flex items-center gap-1 px-2 py-1 bg-green-700/60 text-green-200 rounded text-xs hover:bg-green-600/70 transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Apply to cell
              </button>
              <button
                onClick={() => navigator.clipboard.writeText(result.code_suggestion!)}
                className="flex items-center gap-1 px-2 py-1 bg-gray-700/60 text-gray-300 rounded text-xs hover:bg-gray-600/70 transition-colors"
              >
                Copy
              </button>
            </div>
          </div>
        )}
        <div className="text-[10px] text-gray-500 pt-0.5">
          Haiku 4.5 · {result.usage.latency_ms}ms · ${result.usage.cost_usd.toFixed(4)}
        </div>
      </div>
    )}
  </div>
);

// Inline AI error diagnosis for CodeMirrorCell output area
const ErrorOutput: React.FC<{
  output: any;
  cellSource?: string;
  onApplyFix?: (code: string) => void;
  onInsertCellAbove?: (code: string) => void;
}> = ({ output, cellSource, onApplyFix, onInsertCellAbove }) => {
  const [diagnosis, setDiagnosis] = useState<{
    explanation: string;
    suggestion: string;
    fixed_code?: string | null;
    prerequisite_code?: string | null;
    usage: { latency_ms: number; cost_usd: number };
  } | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  const cleanTraceback = output.traceback?.map((line: string) =>
    line.replace(/\x1b\[[0-9;]*m/g, '')
  );

  const handleDiagnose = async () => {
    if (!cellSource || isAnalyzing) return;
    setIsAnalyzing(true);
    setError(null);
    setDiagnosis(null);

    try {
      const token = localStorage.getItem('authToken');
      const { getApiUrl } = await import('../../../utils/api');
      const res = await fetch(`${getApiUrl()}/api/v1/ai/cell/diagnose-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          code: cellSource,
          error_name: output.ename || 'Error',
          error_value: output.evalue || '',
          traceback: output.traceback || [],
        }),
      });

      if (res.status === 402) {
        setError('Insufficient balance for AI features.');
        return;
      }
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Request failed (${res.status})`);
      }

      setDiagnosis(await res.json());
    } catch (e: any) {
      setError(e.message || 'AI diagnosis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="text-red-400">
      <div className="font-semibold font-mono">
        {output.ename}: {output.evalue}
      </div>
      {cleanTraceback && (
        <pre className="mt-2 text-xs opacity-75 font-mono bg-red-950/30 p-2 rounded overflow-x-auto">
          {cleanTraceback.join('\n')}
        </pre>
      )}

      {cellSource && (
        <div className="mt-3">
          {!diagnosis && !isAnalyzing && !error && (
            <button
              onClick={handleDiagnose}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-700/60 text-purple-200 rounded text-xs hover:bg-purple-600/70 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Fix with AI
            </button>
          )}

          {isAnalyzing && (
            <div className="flex items-center gap-2 text-xs text-purple-300">
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Analyzing error...
            </div>
          )}

          {error && (
            <div className="text-xs text-red-300 bg-red-950/50 rounded px-3 py-2">
              {error}
              <button onClick={() => setError(null)} className="ml-2 underline hover:no-underline">Dismiss</button>
            </div>
          )}

          {diagnosis && (
            <div className="bg-purple-950/40 border border-purple-800/30 rounded-lg p-3 mt-1 space-y-2">
              <div className="text-xs text-purple-200">
                <span className="font-semibold text-purple-300">Explanation:</span>{' '}
                {diagnosis.explanation}
              </div>
              <div className="text-xs text-purple-200">
                <span className="font-semibold text-purple-300">Suggestion:</span>{' '}
                {diagnosis.suggestion}
              </div>

              {diagnosis.fixed_code && onApplyFix && (
                <div className="mt-2">
                  <div className="text-xs text-gray-400 mb-1">Corrected code:</div>
                  <pre className="text-xs bg-gray-900 text-gray-200 p-2 rounded overflow-x-auto mb-2">
                    {diagnosis.fixed_code}
                  </pre>
                  {!applied ? (
                    <button
                      onClick={() => { onApplyFix(diagnosis.fixed_code!); setApplied(true); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-700/60 text-green-200 rounded text-xs hover:bg-green-600/70 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Apply Fix
                    </button>
                  ) : (
                    <span className="text-xs text-green-400">Fix applied — re-run the cell</span>
                  )}
                </div>
              )}

              {diagnosis.prerequisite_code && onInsertCellAbove && (
                <div className="mt-2">
                  <div className="text-xs text-gray-400 mb-1">Run this first:</div>
                  <pre className="text-xs bg-gray-900 text-gray-200 p-2 rounded overflow-x-auto mb-2">
                    {diagnosis.prerequisite_code}
                  </pre>
                  {!applied ? (
                    <button
                      onClick={() => { onInsertCellAbove(diagnosis.prerequisite_code!); setApplied(true); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-700/60 text-blue-200 rounded text-xs hover:bg-blue-600/70 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m-8-8h16" />
                      </svg>
                      Insert Cell Above
                    </button>
                  ) : (
                    <span className="text-xs text-blue-400">Cell inserted — run it, then re-run this cell</span>
                  )}
                </div>
              )}

              <div className="text-xs text-gray-500 pt-1">
                Haiku 4.5 · {diagnosis.usage.latency_ms}ms · ${diagnosis.usage.cost_usd.toFixed(4)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};